import { useEffect, useRef, useState } from "react";
import generatePDF from "react-to-pdf";

import { Box } from "@mui/material";

import PdfReport from "./PdfReport";

const SETTLE_DELAY_MS = 3000;
const GENERATION_TIMEOUT_MS = 30000;
const TIMEOUT_MESSAGE = "PDF generation timed out";
const DOWNLOAD_FAILED_MESSAGE =
  "PDF generated but the download could not start";

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = Object.assign(document.createElement("a"), {
    href: url,
    download: filename
  });
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const generateWithTimeout = (targetRef) =>
  Promise.race([
    generatePDF(targetRef, {
      method: "build",
      page: { format: "letter" }
    }),
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error(TIMEOUT_MESSAGE)),
        GENERATION_TIMEOUT_MS
      )
    )
  ]);

const PdfReportRenderer = ({
  reportProps,
  generationId,
  onCompleted,
  onError
}) => {
  const targetRef = useRef(null);
  const [settled, setSettled] = useState(false);
  const startedForYearRef = useRef(null);
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);
  onCompletedRef.current = onCompleted;
  onErrorRef.current = onError;

  useEffect(() => {
    setSettled(false);
    const timer = setTimeout(() => setSettled(true), SETTLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [reportProps.year]);

  useEffect(() => {
    if (!settled || startedForYearRef.current === reportProps.year) {
      return;
    }
    startedForYearRef.current = reportProps.year;

    const generate = async () => {
      try {
        const pdf = await generateWithTimeout(targetRef);
        const blob = pdf.output("blob");
        const filename = `aclymate-footprint-report-${reportProps.year}.pdf`;
        try {
          triggerBlobDownload(blob, filename);
        } catch (downloadErr) {
          const wrappedErr = new Error(DOWNLOAD_FAILED_MESSAGE);
          wrappedErr.cause = downloadErr;
          onErrorRef.current(wrappedErr);
          return;
        }
        onCompletedRef.current(generationId);
      } catch (err) {
        onErrorRef.current(err);
      }
    };

    generate();
  }, [settled, reportProps.year, generationId]);

  return (
    <Box
      style={{
        position: "absolute",
        left: "-9999px",
        top: 0,
        width: "816px",
        overflow: "hidden"
      }}
    >
      <Box ref={targetRef}>
        <PdfReport {...reportProps} />
      </Box>
    </Box>
  );
};

export { TIMEOUT_MESSAGE, DOWNLOAD_FAILED_MESSAGE };
export default PdfReportRenderer;
